import { useState, useEffect, useCallback } from 'react';
import { defectsApi, DefectPin } from './DefectsApiService';

interface UseDefectsReturn {
  defects: DefectPin[];
  loading: boolean;
  error: string | null;
  createDefect: (defectData: Partial<DefectPin>) => Promise<DefectPin>;
  updateDefect: (id: string, defectData: Partial<DefectPin>) => Promise<DefectPin>;
  deleteDefect: (id: string) => Promise<void>;
  refreshDefects: () => Promise<void>;
}

export const useDefects = (drawingId: string, projectId?: string): UseDefectsReturn => {
  const [defects, setDefects] = useState<DefectPin[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch defects
  const fetchDefects = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await defectsApi.getDefects(drawingId, projectId);
      setDefects(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch defects');
    } finally {
      setLoading(false);
    }
  }, [drawingId, projectId]);

  // Create defect
  const createDefect = useCallback(async (defectData: Partial<DefectPin>): Promise<DefectPin> => {
    setLoading(true);
    setError(null);

    try {
      const newDefect = await defectsApi.createDefect(defectData);
      setDefects(prev => [...prev, newDefect]);
      return newDefect;
    } catch (err: any) {
      setError(err.message || 'Failed to create defect');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update defect
  const updateDefect = useCallback(async (id: string, defectData: Partial<DefectPin>): Promise<DefectPin> => {
    setLoading(true);
    setError(null);

    try {
      const updatedDefect = await defectsApi.updateDefect(id, defectData);
      setDefects(prev => prev.map(d => d.id === id ? updatedDefect : d));
      return updatedDefect;
    } catch (err: any) {
      setError(err.message || 'Failed to update defect');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Delete defect
  const deleteDefect = useCallback(async (id: string): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      await defectsApi.deleteDefect(id);
      setDefects(prev => prev.filter(d => d.id !== id));
    } catch (err: any) {
      setError(err.message || 'Failed to delete defect');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Load defects on mount and when drawingId changes
  useEffect(() => {
    if (drawingId) {
      fetchDefects();
    }
  }, [fetchDefects, drawingId]);

  return {
    defects,
    loading,
    error,
    createDefect,
    updateDefect,
    deleteDefect,
    refreshDefects: fetchDefects,
  };
};